import { motion } from "motion/react";
import { Eye, UserCheck, ShoppingBag, Zap } from "lucide-react";
import { useState } from "react";

export function IdentitySection() {
  const [selectedTab, setSelectedTab] = useState(0);

  const journeyStages = [
    { stage: "Visitor", icon: Eye, description: "IP anônimo navega no site", color: "#666" },
    { stage: "Lead", icon: UserCheck, description: "Preenche formulário - Identidade capturada", color: "#00F5FF" },
    { stage: "Customer", icon: ShoppingBag, description: "Realiza compra - Ciclo fechado", color: "#10B981" },
  ];

  const timelineEvents = [
    { time: "15:42", event: "Page Visit", page: "/produto-smartphone-x", color: "#00F5FF" },
    { time: "15:45", event: "Add to Cart", page: "Smartphone X - R$ 2.499", color: "#FF4D00" },
    { time: "15:47", event: "Popup Form", page: "Lead capturado: João Silva", color: "#10B981" },
    { time: "15:50", event: "Checkout", page: "Iniciou pagamento", color: "#00F5FF" },
    { time: "15:52", event: "Abandoned", page: "Saiu na etapa 2/3", color: "#FF4D00" },
    { time: "16:30", event: "Email Sent", page: "Webhook triggered", color: "#10B981" },
    { time: "18:15", event: "Return Visit", page: "Voltou via email", color: "#00F5FF" },
    { time: "18:20", event: "Purchase", page: "Conversão confirmada!", color: "#10B981" },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,245,255,0.1),transparent_50%)]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Zap className="w-8 h-8 text-[#00F5FF]" />
              <span className="font-['Roboto_Mono'] text-[#00F5FF] text-sm tracking-[0.3em] uppercase">
                O Diferencial que Mata
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Identity Resolution</span>
              <br />
              <span className="text-[#00F5FF] drop-shadow-[0_0_40px_rgba(0,245,255,0.6)]">
                V32
              </span>
              <span className="text-white">: O "Nome do Jogo"</span>
            </h2>
            <p className="font-['Inter'] text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Aqui é onde separamos os meninos dos homens.
            </p>
            <p className="font-['Sora'] text-2xl text-white max-w-2xl mx-auto">
              A maioria dos plugins rastreia <span className="text-gray-400">"sessões"</span>.
              <br />
              O WP Total Tracker rastreia{" "}
              <span className="text-[#10B981] font-bold drop-shadow-[0_0_20px_rgba(16,185,129,0.5)]">
                Pessoas
              </span>
              .
            </p>
          </motion.div>

          {/* PhD Level Architecture */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0B0E14]/80 backdrop-blur-xl border border-[#00F5FF]/30 rounded-3xl p-10 mb-16"
            style={{
              boxShadow: "0 0 60px rgba(0,245,255,0.2), inset 0 0 80px rgba(0,245,255,0.05)",
            }}
          >
            <div className="text-center mb-10">
              <h3 className="font-['Roboto_Mono'] text-[#00F5FF] text-lg tracking-wider mb-4">
                ARQUITETURA V32 (PhD LEVEL)
              </h3>
              <p className="font-['Inter'] text-gray-300 text-lg max-w-3xl mx-auto">
                Sistema de <span className="text-[#00F5FF] font-semibold">Context Resolution</span> avançado que transforma dados fragmentados em perfis completos.
              </p>
            </div>

            {/* Journey Stages */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {journeyStages.map((stage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative"
                >
                  <div
                    className="bg-[#0B0E14]/90 border-2 rounded-2xl p-8 text-center relative"
                    style={{
                      borderColor: stage.color,
                      boxShadow: `0 0 30px ${stage.color}40`,
                    }}
                  >
                    {/* Icon */}
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{
                        backgroundColor: `${stage.color}20`,
                        boxShadow: `0 0 30px ${stage.color}50`,
                      }}
                    >
                      <stage.icon className="w-10 h-10" style={{ color: stage.color }} />
                    </div>

                    {/* Stage Name */}
                    <h4
                      className="font-['Sora'] text-2xl font-bold mb-3"
                      style={{ color: stage.color }}
                    >
                      {stage.stage}
                    </h4>

                    {/* Description */}
                    <p className="font-['Inter'] text-sm text-gray-400">
                      {stage.description}
                    </p>

                    {/* Stage Label */}
                    <div className="mt-4">
                      <span
                        className="font-['Roboto_Mono'] text-xs px-4 py-2 rounded-full"
                        style={{
                          backgroundColor: `${stage.color}20`,
                          color: stage.color,
                          border: `1px solid ${stage.color}40`,
                        }}
                      >
                        {index === 1 ? "Boom. Identidade capturada" : index === 2 ? "Bingo. Ciclo fechado" : "Estado Inicial"}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  {index < 2 && (
                    <motion.div
                      className="hidden md:block absolute top-1/2 -right-4 text-4xl text-[#00F5FF]"
                      initial={{ x: -10, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.2 }}
                    >
                      →
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* User Profile Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-[#0B0E14]/90 to-[#0B0E14]/70 backdrop-blur-2xl border border-[#00F5FF]/40 rounded-3xl overflow-hidden"
            style={{
              boxShadow: "0 0 80px rgba(0,245,255,0.25), inset 0 0 100px rgba(0,245,255,0.08)",
            }}
          >
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-[#00F5FF]/20 to-[#10B981]/20 border-b border-[#00F5FF]/30 p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Avatar */}
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00F5FF] to-[#10B981] flex items-center justify-center text-3xl font-bold text-[#0B0E14]">
                    JS
                  </div>
                  <div>
                    <h3 className="font-['Sora'] text-3xl font-bold text-white mb-2">
                      João Silva
                    </h3>
                    <p className="font-['Roboto_Mono'] text-sm text-gray-400">
                      joao.silva@email.com • ID: #USER_8492
                    </p>
                  </div>
                </div>

                {/* Engagement Score */}
                <div className="text-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#00F5FF20"
                        strokeWidth="8"
                        fill="none"
                      />
                      <motion.circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#00F5FF"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 56}`}
                        initial={{ strokeDashoffset: 2 * Math.PI * 56 }}
                        whileInView={{ strokeDashoffset: 2 * Math.PI * 56 * 0.15 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        style={{ filter: "drop-shadow(0 0 10px #00F5FF)" }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center flex-col">
                      <span className="font-['Sora'] text-3xl font-bold text-[#00F5FF]">85</span>
                      <span className="font-['Roboto_Mono'] text-xs text-gray-400">Score</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead Lifeline */}
            <div className="p-8">
              <h4 className="font-['Sora'] text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-[#00F5FF] rounded-full" />
                Lead Lifeline: Jornada Completa
              </h4>

              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00F5FF] via-[#FF4D00] to-[#10B981] rounded-full" />

                {/* Timeline Events */}
                <div className="space-y-6">
                  {timelineEvents.map((event, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="relative flex items-start gap-6"
                    >
                      {/* Time Dot */}
                      <div
                        className="w-4 h-4 rounded-full border-4 border-[#0B0E14] z-10"
                        style={{
                          backgroundColor: event.color,
                          boxShadow: `0 0 20px ${event.color}80`,
                        }}
                      />

                      {/* Event Card */}
                      <div
                        className="flex-1 bg-[#0B0E14]/80 backdrop-blur-lg border rounded-xl p-4 hover:scale-105 transition-transform"
                        style={{
                          borderColor: `${event.color}40`,
                          boxShadow: `0 0 20px ${event.color}20`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className="font-['Roboto_Mono'] text-xs px-3 py-1 rounded-full"
                            style={{
                              backgroundColor: `${event.color}20`,
                              color: event.color,
                            }}
                          >
                            {event.event}
                          </span>
                          <span className="font-['Roboto_Mono'] text-xs text-gray-500">
                            {event.time}
                          </span>
                        </div>
                        <p className="font-['Inter'] text-sm text-gray-300">
                          {event.page}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="mt-10 bg-[#10B981]/10 border border-[#10B981]/40 rounded-xl p-6"
              >
                <p className="font-['Inter'] text-lg text-white text-center">
                  <span className="text-[#10B981] font-bold">Resultado:</span> Visitante anônimo → Lead capturado → Cliente convertido em 2h38min
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="font-['Sora'] text-2xl text-white mb-4">
              Você não perde o histórico.
            </p>
            <p className="font-['Sora'] text-3xl font-bold text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
              O histórico é eterno e acessível.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
